import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, patch, del } from '@/lib/api'

import type { MilestoneStatus } from '@company/shared'


// ==================== Types ====================

export type { MilestoneStatus }

export interface Milestone {
  id: string
  projectId: string
  name: string
  description: string | null
  dueDate: string | null
  status: MilestoneStatus
  completedAt: string | null
  sortOrder: number | null
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface MilestoneWithProgress extends Milestone {
  totalIssues: number
  completedIssues: number
  progress: number
}

export interface MilestoneListParams {
  status?: MilestoneStatus
  sortBy?: 'createdAt' | 'dueDate' | 'name'
  sortOrder?: 'asc' | 'desc'
}

export interface CreateMilestoneInput {
  name: string
  description?: string
  dueDate?: string
}

export interface UpdateMilestoneInput {
  name?: string
  description?: string
  dueDate?: string
  status?: MilestoneStatus
}

// ==================== Query Keys ====================

export const milestoneKeys = {
  all: ['milestones'] as const,
  lists: () => [...milestoneKeys.all, 'list'] as const,
  list: (projectId: string, params: MilestoneListParams) => [...milestoneKeys.lists(), projectId, params] as const,
  details: () => [...milestoneKeys.all, 'detail'] as const,
  detail: (id: string) => [...milestoneKeys.details(), id] as const,
}

// ==================== Hooks ====================

/**
 * 获取项目里程碑列表
 */
export function useMilestones(projectId: string, params: MilestoneListParams = {}) {
  return useQuery({
    queryKey: milestoneKeys.list(projectId, params),
    queryFn: () => get<MilestoneWithProgress[]>(`/projects/${projectId}/milestones`, params as Record<string, unknown>),
    enabled: !!projectId,
  })
}

/**
 * 获取单个里程碑
 */
export function useMilestone(id: string) {
  return useQuery({
    queryKey: milestoneKeys.detail(id),
    queryFn: () => get<MilestoneWithProgress>(`/milestones/${id}`),
    enabled: !!id,
  })
}

/**
 * 创建里程碑
 */
export function useCreateMilestone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: CreateMilestoneInput }) =>
      post<Milestone>(`/projects/${projectId}/milestones`, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: milestoneKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] })
    },
  })
}

/**
 * 更新里程碑
 */
export function useUpdateMilestone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMilestoneInput }) =>
      patch<Milestone>(`/milestones/${id}`, data),
    onSuccess: (milestone) => {
      void queryClient.invalidateQueries({ queryKey: milestoneKeys.detail(milestone.id) })
      void queryClient.invalidateQueries({ queryKey: milestoneKeys.lists() })
    },
  })
}

/**
 * 删除里程碑
 */
export function useDeleteMilestone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/milestones/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: milestoneKeys.lists() })
    },
  })
}
